import { useCallback, useEffect, useRef, useState } from 'react';
import { prefersReducedMotion } from '../../../../utils/prefersReducedMotion.ts';
const SCROLL_EDGE_THRESHOLD = 1;
const ACTIVE_PANEL_TOP_OFFSET = 8;
const PROGRAMMATIC_SCROLL_RESET_MS = 600;
export const useVerticalTabsScroll = ({ activeIndex, activeTabId, onTabChange, tabs })=>{
    const scrollContainerRef = useRef(null);
    const panelRefs = useRef([]);
    const hasScrolledToInitialPanelRef = useRef(false);
    const isProgrammaticScrollRef = useRef(false);
    const programmaticScrollTimeoutRef = useRef(0);
    const [shouldFadeStart, setShouldFadeStart] = useState(true);
    const armProgrammaticScrollReset = useCallback(()=>{
        if (programmaticScrollTimeoutRef.current != 0) clearTimeout(programmaticScrollTimeoutRef.current);
        programmaticScrollTimeoutRef.current = window.setTimeout(()=>{
            isProgrammaticScrollRef.current = false;
            programmaticScrollTimeoutRef.current = 0;
            setShouldFadeStart(false);
        }, PROGRAMMATIC_SCROLL_RESET_MS);
    }, []);
    const scrollToPanel = useCallback((index, behavior = 'smooth')=>{
        const container = scrollContainerRef.current;
        if (container == null) return false;
        const panel = panelRefs.current[index];
        if (panel == null) return false;
        isProgrammaticScrollRef.current = true;
        armProgrammaticScrollReset();
        const scrollBehavior = prefersReducedMotion() ? 'auto' : behavior;
        container.scrollTo({
            top: panel.offsetTop,
            behavior: scrollBehavior
        });
        return true;
    }, [
        armProgrammaticScrollReset
    ]);
    useEffect(()=>{
        if (hasScrolledToInitialPanelRef.current) return;
        if (!scrollToPanel(activeIndex, 'auto')) return;
        hasScrolledToInitialPanelRef.current = true;
    }, [
        activeIndex,
        scrollToPanel
    ]);
    useEffect(()=>{
        const container = scrollContainerRef.current;
        if (container == null) return;
        let pendingFrame = 0;
        const updateActivePanel = ()=>{
            var _tabs_nextIndex;
            pendingFrame = 0;
            if (isProgrammaticScrollRef.current) return;
            const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
            const reachedBottom = distanceFromBottom <= SCROLL_EDGE_THRESHOLD;
            let nextIndex = 0;
            panelRefs.current.forEach((panel, index)=>{
                if (panel == null) return;
                if (panel.offsetTop - container.scrollTop <= ACTIVE_PANEL_TOP_OFFSET) nextIndex = index;
            });
            if (reachedBottom) nextIndex = tabs.length - 1;
            const nextTabId = (_tabs_nextIndex = tabs[nextIndex]) === null || _tabs_nextIndex === void 0 ? void 0 : _tabs_nextIndex.props.id;
            if (nextTabId == undefined) return;
            if (nextTabId == activeTabId) return;
            onTabChange(nextTabId);
        };
        const scheduleUpdate = ()=>{
            if (isProgrammaticScrollRef.current) armProgrammaticScrollReset();
            else setShouldFadeStart(true);
            if (pendingFrame != 0) return;
            pendingFrame = requestAnimationFrame(updateActivePanel);
        };
        const handleScrollEnd = ()=>{
            const wasProgrammaticScroll = isProgrammaticScrollRef.current;
            isProgrammaticScrollRef.current = false;
            if (wasProgrammaticScroll) setShouldFadeStart(false);
            if (programmaticScrollTimeoutRef.current != 0) {
                clearTimeout(programmaticScrollTimeoutRef.current);
                programmaticScrollTimeoutRef.current = 0;
            }
        };
        container.addEventListener('scroll', scheduleUpdate, {
            passive: true
        });
        container.addEventListener('scrollend', handleScrollEnd);
        return ()=>{
            container.removeEventListener('scroll', scheduleUpdate);
            container.removeEventListener('scrollend', handleScrollEnd);
            if (pendingFrame != 0) cancelAnimationFrame(pendingFrame);
        };
    }, [
        tabs,
        activeTabId,
        onTabChange,
        armProgrammaticScrollReset
    ]);
    return {
        panelRefs: panelRefs,
        scrollContainerRef: scrollContainerRef,
        scrollToPanel: scrollToPanel,
        shouldFadeStart: shouldFadeStart
    };
};
